/**
 * CommentPost Component
 *
 * Renders the main content of a forum comment, including user info, message, likes, replies, and edit functionality.
 * Handles like, reply, edit, and show answers actions for comments and subcomments.
 *
 * Features:
 * - Displays user image and name
 * - Shows comment message and allows editing
 * - Handles like/unlike actions for comments and subcomments
 * - Allows replying and viewing answers for comments
 * - Passes relevant props to child components for actions
 * - Uses Material-UI for UI elements and CSS modules for styling
 *
 * @param {Object} props - Component properties
 * @param {Object} [props.commentData] - Data object for the comment
 * @param {function} [props.responseComment] - Function to trigger reply input
 * @param {bool} [props.editComment] - Indicates if the comment is in edit mode
 * @param {function} [props.showAnswers] - Function to show answers
 * @param {number} [props.idComment] - ID of the comment or subcomment
 * @param {function} [props.editCommentHandler] - Function to trigger edit mode
 * @param {bool} [props.subcomment] - Indicates if the comment is a subcomment
 * @returns {JSX.Element} Rendered CommentPost component
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import TextField from '@mui/material/TextField';

import {
  editComment,
  editSubcomment,
  likeAnswerFunction,
  likePostFunction
} from 'store/lesson-actions';

import classes from './CommentPost.module.css';

const CommentPost = props => {
  const { t } = useTranslation(['forum']);
  const [postText, setPostText] = useState(
    props.commentData.message
  );
  const [likeComment, setLikeComment] = useState(
    props.commentData.userLike === 1 ? true : false
  );
  const [likes, setLikes] = useState(props.commentData.likes);

  /**
   * Handles like/unlike action for comment or subcomment
   */
  const likeCommentHandler = () => {
    setLikeComment(!likeComment);
    if (!likeComment === false) {
      setLikes(likes - 1);
    } else {
      setLikes(likes + 1);
    }
    if (props.subcomment) {
      (async () => {
        await likeAnswerFunction(props.idComment, !likeComment);
      })();
    } else {
      (async () => {
        await likePostFunction(props.idComment, !likeComment);
      })();
    }
  };

  /**
   * Triggers reply input for the comment
   */
  const showResponseCommentHandler = () => {
    props.responseComment();
  };

  /**
   * Shows answers for the comment
   */
  const showAnswersHandler = () => {
    props.showAnswers();
  };

  /**
   * Handles text change for editing comment
   */
  const handleChange = event => {
    setPostText(event.target.value);
  };

  /**
   * Handles edit action for comment or subcomment
   */
  const editPostHandler = () => {
    (async () => {
      if (props.subcomment) {
        await editSubcomment(props.idComment, postText);
      } else {
        await editComment(props.idComment, postText);
      }
      props.editCommentHandler();
    })();
  };

  return (
    <>
      {/* User image section */}
      <figure className={classes.userImageContainer}>
        {props.commentData.urlImage ? (
          <img
            className={classes.userImage}
            src={props.commentData.urlImage}
            alt={props.commentData.fullname}
          />
        ) : (
          <img
            className={classes.userImage}
            src="https://d1g6c01s9ssd7h.cloudfront.net/img/usuario12.png"
            alt={props.commentData.fullname}
          />
        )}
      </figure>
      {/* User name section */}
      <p className={classes.userNameComment}>
        {props.commentData.fullname}
      </p>
      {/* Material-UI TextField for comment message */}
      <TextField
        multiline
        value={postText}
        disabled={!props.editComment}
        className={classes.forumInput}
        onChange={handleChange}
      />
      <div className={classes.complementaryInfoContainer}>
        {!props.editComment ? (
          <>
            {/* Like button for comment/subcomment */}
            {likeComment ? (
              <IconButton
                className={classes.likeBtn}
                onClick={likeCommentHandler}
              >
                <ThumbUpIcon className={classes.likeIcon} />
              </IconButton>
            ) : (
              <IconButton
                className={classes.likeBtn}
                onClick={likeCommentHandler}
              >
                <ThumbUpOutlinedIcon className={classes.likeIcon} />
              </IconButton>
            )}
            <span>
              {likes} {!props.subcomment && '-'}
            </span>
            {/* Reply and see answers buttons for main comments */}
            {!props.subcomment && (
              <>
                <Button
                  className={classes.complementaryBtn}
                  onClick={showResponseCommentHandler}
                >
                  {t('reply')}
                </Button>
                <Button
                  className={classes.complementaryBtn}
                  disabled={props.commentData.comments === 0}
                  onClick={showAnswersHandler}
                >
                  {t('see')} {props.commentData.comments}{' '}
                  {t('answers')}
                </Button>
              </>
            )}
          </>
        ) : (
          <Button
            className={classes.editPostBtn}
            onClick={editPostHandler}
          >
            {t('edit')}
          </Button>
        )}
      </div>
    </>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 */
CommentPost.propTypes = {
  /** Data object for the comment */
  commentData: PropTypes.object,
  /** Function to trigger reply input */
  responseComment: PropTypes.func,
  /** Indicates if the comment is in edit mode */
  editComment: PropTypes.bool,
  /** Function to show answers */
  showAnswers: PropTypes.func,
  /** ID of the comment or subcomment */
  idComment: PropTypes.number,
  /** Function to trigger edit mode */
  editCommentHandler: PropTypes.func,
  /** Indicates if the comment is a subcomment */
  subcomment: PropTypes.bool
};

CommentPost.defaultProps = {
  commentData: undefined,
  responseComment: undefined,
  editComment: undefined,
  showAnswers: undefined,
  idComment: undefined,
  editCommentHandler: undefined,
  subcomment: undefined
};

export default CommentPost;
